import React, { useContext, useEffect, useRef, useState } from "react";
import { X, Eye, Calendar, Heart } from "lucide-react";
import { AuthContext } from "context/AuthContext";
import FullScreenDialog from "modules/swiftboard/components/ui/FullScreenDialog";
import Skeleton from "components/ui/Skeleton";
import ScrollToTopBottom from "modules/swiftboard/components/ui/ScrollToTopBottom";
import Avatar from "components/ui/Avatar";
import RoleBadge from "modules/swiftboard/components/ui/RoleBadge";
import LocalDateTime, { getRelativeTime } from "services/date-converter";
import ArticleHero from "modules/swiftboard/knowledge-base/article-view/ArticleHero";
import InteractiveBlock from "modules/swiftboard/knowledge-base/article-view/InteractiveBlock";
import AttachmentsList from "modules/swiftboard/knowledge-base/article-view/AttachmentsList";
import useArticleInteractions from "modules/swiftboard/knowledge-base/article-view/useArticleInteractions";
import apiKnowledgeArticle from "../api";
import { getDisplayIdentity } from "utils/user-identity";

// "Liked by" panel — owned entirely by this admin preview (not the shared
// article-view components), and rendered *inside* the dialog's own body
// (absolute within the same relative wrapper the scroll area sits in), so
// it reads as part of this dialog rather than a second full-screen overlay
// stacked on top of it. ADMIN/SUPER_ADMIN only — enforced again server-side
// by getContentLikers regardless of what this UI does.
const LikersPanel = ({ contentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [likers, setLikers] = useState([]);

  useEffect(() => {
    if (!contentId) return;
    setLoading(true);
    apiKnowledgeArticle
      .getLikers(contentId)
      .then((res) => {
        if (res.data.status) setLikers(res.data.data || []);
      })
      .finally(() => setLoading(false));
  }, [contentId]);

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm max-h-[70vh] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl overflow-hidden"
      >
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Heart size={14} className="text-danger fill-current" />
            <h3 className="text-[13.5px] font-bold text-text_primary">
              Liked by{likers.length > 0 ? ` (${likers.length})` : ""}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-text_muted" title="Close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <p className="p-4 text-center text-xs text-text_muted">Loading...</p>
          ) : likers.length === 0 ? (
            <p className="p-6 text-center text-xs text-text_muted">No likes yet.</p>
          ) : (
            <div className="space-y-0.5">
              {likers.map((u) => {
                const identity = getDisplayIdentity(u);
                return (
                  <div key={u.user_id} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg">
                    <Avatar
                      user={identity?.avatarUser}
                      size={32}
                      fit={identity?.isBrand ? "contain" : "cover"}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[13px] font-semibold text-text_primary truncate">
                          {identity?.name}
                        </span>
                        <RoleBadge role={identity?.role} size="sm" />
                      </div>
                      <p className="text-[10.5px] text-text_muted">{getRelativeTime(u.liked_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Reader preview for the admin article list — reuses the shared
// knowledge_hub API + interactive components (ArticleHero, InteractiveBlock,
// comments, likes) the real client-facing reader page runs on, so this is
// never a stale/simplified stand-in for what users actually see. On top of
// that it surfaces engagement (likes/comments) and, when the viewer is
// ADMIN/SUPER_ADMIN (article.can_moderate), moderation controls: pin a
// top-level comment (max 3 per block), delete anyone's comment, and see
// exactly who liked a block (the LikersPanel above, owned by this file).
const ArticlePreviewDialog = ({ articleId, isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const scrollRef = useRef(null);
  const [likersContentId, setLikersContentId] = useState(null);
  const {
    loading,
    article,
    fetchArticle,
    handleToggleLike,
    handleAddComment,
    handleDeleteComment,
    handlePinComment,
  } = useArticleInteractions(articleId);

  useEffect(() => {
    if (!isOpen || !articleId) return;
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, articleId]);

  const isLive = !!(article?.status === "PUBLISHED" && article?.is_activated);

  return (
    <FullScreenDialog isOpen={isOpen} onClose={onClose}>
      <div className="h-full flex flex-col overflow-hidden bg-surface_alt">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-3.5 border-b border-border bg-surface">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary_tint flex items-center justify-center flex-shrink-0">
              <Eye size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-text_primary">Reader Preview</p>
              <p className="text-[11px] text-text_muted truncate">Exactly how this article will appear to users</p>
            </div>
          </div>
          {!loading && article && !isLive && (
            <span className="hidden sm:inline-flex flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border bg-warning_bg text-warning border-warning_border">
              Not visible to users yet
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-lg text-text_muted"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 relative min-h-0">
          <div ref={scrollRef} className="h-full overflow-y-auto">
            {loading ? (
              <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
                <Skeleton className="h-56 w-full" />
                <Skeleton className="h-8 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : !article ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-text_muted">Couldn't load this article.</p>
              </div>
            ) : (
              <>
                <ArticleHero article={article} />

                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 min-w-0 break-normal [overflow-wrap:break-word] [word-break:normal] [hyphens:none]">
                  <div className="flex items-center gap-1.5 text-xs text-text_muted pb-6 mb-6 border-b border-border">
                    <Calendar size={12} />
                    Last updated <LocalDateTime utcTime={article.updated_date} type="date" />
                  </div>

                  <div className="space-y-4">
                    {article.kb_contents.length === 0 ? (
                      <p className="text-sm text-text_muted italic">This article has no content yet.</p>
                    ) : (
                      article.kb_contents.map((block) => (
                        <InteractiveBlock
                          key={block.id}
                          block={block}
                          currentUser={user}
                          canModerate={article.can_moderate}
                          onToggleLike={handleToggleLike}
                          onAddComment={handleAddComment}
                          onDeleteComment={handleDeleteComment}
                          onPinComment={handlePinComment}
                          renderLikeExtra={
                            article.can_moderate
                              ? (b) => (
                                  <button
                                    type="button"
                                    onClick={() => setLikersContentId(b.id)}
                                    title="See who liked this"
                                    className="text-xs font-semibold text-text_muted"
                                  >
                                    {b.likes_preview?.total_likes ?? b.engagement?.total_likes ?? 0}
                                  </button>
                                )
                              : undefined
                          }
                        />
                      ))
                    )}
                  </div>

                  <AttachmentsList attachments={article.kb_attachments} />
                </div>
              </>
            )}
          </div>

          <ScrollToTopBottom scrollRef={scrollRef} />

          {likersContentId && (
            <LikersPanel contentId={likersContentId} onClose={() => setLikersContentId(null)} />
          )}
        </div>
      </div>
    </FullScreenDialog>
  );
};

export default ArticlePreviewDialog;
